import { memo } from "react"
import { useSelector } from "react-redux"
import AlertCell from "@/components/AlertCell"
import CellCard, { UserStatus } from "@/components/CellCard"
import { RootState } from "@/store"
import { cellsSelectors } from "@/store/cellsSlice"
import { Alert, AlertVariant, TypefaceVariant } from "@/types/alert"
import { getHighestPrioAlert } from "@/utils/cells"
import { get } from "@/utils/lodash"

interface CellCardItemProps {
   id: string
   onClearAlertClick: (alert: Alert) => void
   onNavigateToCellDetail: (cellId: string) => void
   onChangeCellStatus: (
      cellId: string,
      status: boolean,
      isMaintenanceField?: boolean
   ) => void
   onTriggerTestAlarm?: (cellId: string) => void
   isExpanded?: boolean
   isCollapsed?: boolean
   onExpandedChange?: (id: string, isExpanded: boolean) => void
}

const CellCardItem = memo(
   ({
      id,
      onClearAlertClick,
      onNavigateToCellDetail,
      onChangeCellStatus,
      onTriggerTestAlarm,
      isExpanded,
      isCollapsed,
      onExpandedChange,
   }: CellCardItemProps) => {
      const cell = useSelector((state: RootState) =>
         cellsSelectors.selectById(state, id)
      )

      if (!cell) return null

      const hasActiveAlert = cell.alerts && cell.alerts.length > 0

      if (hasActiveAlert) {
         const alert = getHighestPrioAlert(cell.alerts)
         const alertVariant = get(
            alert,
            "alertConfiguration.color.card",
            ""
         ).toLowerCase() as keyof typeof AlertVariant
         const typefaceColor = get(
            alert,
            "alertConfiguration.color.typeface",
            ""
         ).toLowerCase() as keyof typeof TypefaceVariant

         return (
            <AlertCell
               cellId={cell.name}
               hasDoorSensor={cell.hasDoorSensor}
               doorSensorChargeLevel={cell.doorSensorData?.battery}
               systemConditionType={
                  alert?.alertConfiguration.systemCondition?.type
               }
               isCustom={cell.hasCustomConfigurations}
               alertVariant={alertVariant}
               typefaceColor={typefaceColor}
               vitals={cell.alerts?.[0]?.vitals}
               onClearAlert={() => alert && onClearAlertClick(alert)}
               onViewDetails={() => onNavigateToCellDetail(cell.id)}
            />
         )
      }

      return (
         <CellCard
            cellId={cell.name}
            bpm={get(cell, "data.vitals.heartRate")}
            bpmStabilityScore={get(cell, "data.vitals.heartRateStabilityScore")}
            rpm={get(cell, "data.vitals.respiratoryRate")}
            rpmStabilityScore={get(
               cell,
               "data.vitals.respiratoryStabilityScore"
            )}
            occupied={!cell.vacant}
            maintenance={cell.maintenance}
            cellStatus={cell.data.status}
            userStatus={
               get(cell, "data.status.moving")
                  ? UserStatus.MOVING
                  : UserStatus.STABLE
            }
            hasDoorSensor={cell.hasDoorSensor}
            doorSensorChargeLevel={cell.doorSensorData?.battery}
            isCustom={cell.hasCustomConfigurations}
            onSetMaintenanceOff={() => onChangeCellStatus(cell.id, false, true)}
            onSetVacant={() => onChangeCellStatus(cell.id, true)}
            onViewDetails={() => onNavigateToCellDetail(cell.id)}
            onTriggerTestAlarm={
               onTriggerTestAlarm && (() => onTriggerTestAlarm(cell.id))
            }
            isExpanded={isExpanded}
            isCollapsed={isCollapsed}
            onExpandedChange={(nextIsExpanded) =>
               onExpandedChange?.(cell.id, nextIsExpanded)
            }
         />
      )
   }
)

CellCardItem.displayName = "CellCardItem"

export default CellCardItem
